/**
 * Polls /api/store/new-orders-since/ every 20 s. Pops a center-screen
 * modal for each newly-sighted BridgeClubOrder, with a short Web-Audio
 * chime to grab attention. "Dismiss" calls the acknowledge endpoint so
 * the modal won't re-pop after a page refresh. "View Order" navigates
 * to the order detail page.
 *
 * Mount this once in the store-portal layout so it watches every page
 * the cashier is on.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface NewOrder {
  id: number;
  order_number: string;
  customer_name: string;
  phone: string;
  item_count: number;
  total: number;
  payment_status: string;
  pickup_method: string;
  created_at: string;
}

interface Response {
  orders: NewOrder[];
  server_time: string;
}

const STORAGE_KEY_PREFIX = 'storeNewOrders.lastSeen.';
const POLL_MS = 20_000;

const playChime = () => {
  try {
    const ctx = new (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.frequency.value = 880;
    o.type = 'sine';
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
    o.start(ctx.currentTime);
    o.stop(ctx.currentTime + 0.5);
  } catch {
    // No audio context (or user gesture required); skip silently.
  }
};

const NewOrderWatcher: React.FC = () => {
  const navigate = useNavigate();
  const [queue, setQueue] = useState<NewOrder[]>([]);
  const seenIdsRef = useRef<Set<number>>(new Set());
  const userKeyRef = useRef<string>('anon');

  // Pull a stable per-user storage key. Falls back to 'anon' if the
  // user context isn't loaded yet — first poll just won't filter
  // anything beyond server-side acknowledged state.
  useEffect(() => {
    const stored = window.localStorage.getItem('currentUserId');
    if (stored) userKeyRef.current = stored;
  }, []);

  const storageKey = `${STORAGE_KEY_PREFIX}${userKeyRef.current}`;

  const poll = useCallback(async () => {
    const since =
      window.localStorage.getItem(storageKey) ||
      new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    try {
      const res = await api.get<Response>('/store/new-orders-since/', {
        params: { since },
      });
      const fresh: NewOrder[] = [];
      for (const o of res.data.orders) {
        if (!seenIdsRef.current.has(o.id)) {
          seenIdsRef.current.add(o.id);
          fresh.push(o);
        }
      }
      if (fresh.length > 0) {
        setQueue((q) => [...q, ...fresh]);
        playChime();
      }
      window.localStorage.setItem(storageKey, res.data.server_time);
    } catch {
      // Polling failure is non-fatal — try again next tick.
    }
  }, [storageKey]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [poll]);

  const current = queue[0];

  const dismiss = async () => {
    if (!current) return;
    try {
      await api.post(`/store/orders/${current.id}/acknowledge/`);
    } catch {
      // Acknowledging is best-effort; either way drop it from the queue
    }
    setQueue((q) => q.slice(1));
  };

  const viewOrder = async () => {
    if (!current) return;
    // Re-fetch fresh order state before navigating. The modal's
    // payment_status snapshot is taken when the new-orders-since poll
    // first sees the row (up to 20 s ago) and can be stale — the order
    // may have been paid in the meantime by the Gmail parser. The
    // detail page already re-fetches on mount, but updating the
    // modal's in-memory queue lets the next modal in line show fresh
    // data too, and the freshly-painted label is briefly visible.
    try {
      const wideSince = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const refresh = await api.get<Response>('/store/new-orders-since/', {
        params: { since: wideSince },
      });
      const fresh = refresh.data.orders.find((o) => o.id === current.id);
      if (fresh) {
        setQueue((q) => q.map((o) => (o.id === current.id ? { ...o, ...fresh } : o)));
      }
    } catch {
      // Refresh is best-effort — detail page will GET on mount anyway.
    }
    try {
      await api.post(`/store/orders/${current.id}/acknowledge/`);
    } catch {
      // ignore
    }
    const target = `/store/orders/${current.order_number}/`;
    setQueue((q) => q.slice(1));
    navigate(target);
  };

  if (!current) return null;

  const isLocker = current.pickup_method === 'locker';
  // Two non-paid label states: "Pay In Store" (cash collected at till)
  // and "Awaiting Payment" (card order whose Helcim receipt hasn't been
  // recorded yet — Gmail parser will catch it within ~5 min). Both
  // need attention from staff, just differently.
  const isPayInStore = current.payment_status === 'Pay In Store';
  const isAwaitingPayment = current.payment_status === 'Awaiting Payment';
  const needsAttention = isPayInStore || isAwaitingPayment;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          padding: 32,
          minWidth: 380,
          maxWidth: 560,
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <div style={{ fontSize: 16, color: '#6b7280', marginBottom: 4 }}>
          🛒 NEW {isLocker ? 'LOCKER' : 'IN-STORE PICKUP'} ORDER
          {queue.length > 1 && (
            <span style={{ float: 'right' }}>
              · {queue.length - 1} more queued
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: 24,
            fontWeight: 700,
            fontFamily: 'monospace',
            marginBottom: 16,
          }}
        >
          Order #{current.order_number}
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
          {current.customer_name}
        </div>
        <div style={{ color: '#6b7280', marginBottom: 12 }}>
          {current.phone || '(no phone)'}
        </div>
        <div style={{ fontSize: 16, marginBottom: 4 }}>
          {current.item_count} item{current.item_count === 1 ? '' : 's'} ·{' '}
          <strong>${current.total.toFixed(2)}</strong>
        </div>
        <div
          style={{
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: 4,
            fontSize: 13,
            fontWeight: 600,
            background: needsAttention ? '#fef3c7' : '#dcfce7',
            color: needsAttention ? '#92400e' : '#166534',
            marginTop: 8,
            marginBottom: 20,
          }}
        >
          Payment: {current.payment_status}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={viewOrder}
            style={{
              flex: 1,
              background: '#059669',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: 6,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              minHeight: 48,
            }}
          >
            View Order
          </button>
          <button
            onClick={dismiss}
            style={{
              flex: 1,
              background: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              padding: '12px 20px',
              borderRadius: 6,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              minHeight: 48,
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewOrderWatcher;
